import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '@env/environment';
import { SortingPagingData } from '@helpers/sorting-paging-data';
import { CustomerHttpParamsCodec } from '@helpers/custom-http-params-codec';
import { IcashComparisonDetails, IcashComparisonPage } from '../_models/icash-comparison-details.model';

const icashComparisonURL = environment.apiBaseURL + '/icash-comparison';

const pageableComparison = icashComparisonURL + '/pageable';
const comparisonDetails  = icashComparisonURL + '/details';

@Injectable({
  providedIn: 'root'
})
export class IcashComparisonService {

  constructor(private http: HttpClient) { }

  /**
   * One page of the comparison, with the summary counts alongside it.
   *
   * There is no paired count call: the counts fall out of a comparison already done, and asking
   * again would repeat every call out to iCash just to recompute them.
   */
  public findAllByFilter(sortingPaging: SortingPagingData, searchForm: any, fromDate: string, toDate: string): Observable<IcashComparisonPage> {
    return this.http.get<IcashComparisonPage>(pageableComparison,
      { params: this.createSearchHttpRequestParams(sortingPaging, searchForm, fromDate, toDate) });
  }

  /**
   * The line-by-line comparison behind one row.
   *
   * The id names either an inspection or a sale; subjectType says which. The parameter keeps its
   * original name on the wire, where it meant an inspection before sales joined the report.
   */
  public findDetails(id: number, operationId: number,
                     subjectType: 'TECHNICAL_INSPECTION' | 'SALE' = 'TECHNICAL_INSPECTION'): Observable<IcashComparisonDetails> {
    let requestParams = new HttpParams({encoder: new CustomerHttpParamsCodec()});
    requestParams = requestParams.set('technicalInspectionId', String(id));
    requestParams = requestParams.set('operationId', String(operationId));
    requestParams = requestParams.set('subjectType', subjectType);

    return this.http.get<IcashComparisonDetails>(comparisonDetails, { params: requestParams });
  }

  private createSearchHttpRequestParams(sortingPaging: SortingPagingData, searchForm: any, fromDate: string, toDate: string) {
    let requestParams = new HttpParams({encoder: new CustomerHttpParamsCodec()});

    if (sortingPaging != null) {
      requestParams = requestParams.set("page", sortingPaging.getPageNumber());
      requestParams = requestParams.set("pageSize", sortingPaging.getPageSize());
    }

    // Set one by one rather than looping the form: an empty select sends '', which would reach
    // the backend as a blank storeId and fail to bind to an Integer.
    if (searchForm.storeId) {
      requestParams = requestParams.set("storeId", searchForm.storeId);
    }

    // The store drives both sides of the report - iCash by storeId, our inspections by the
    // technical point behind it - so the pair travels together.
    if (searchForm.techPointMobileAppId) {
      requestParams = requestParams.set("techPointMobileAppId", searchForm.techPointMobileAppId);
    }

    if (searchForm.status) {
      requestParams = requestParams.set("status", searchForm.status);
    }

    // Left out when blank, which is what shows both kinds.
    if (searchForm.subjectType) {
      requestParams = requestParams.set("subjectType", searchForm.subjectType);
    }

    if (fromDate && fromDate.length !== 0) {
      requestParams = requestParams.set("dateFrom", fromDate);
    }

    if (toDate && toDate.length !== 0) {
      requestParams = requestParams.set("dateTo", toDate);
    }

    return requestParams;
  }

}
